import { useEffect, useState } from "react";
import type { TranslateFn } from "../../i18n";
import type { Profile, HistoryEntry, DrawerType } from "../types";
import { ArrowLeftIcon, UserIcon, ChevronRightIcon, TrashIcon } from "../icons";
import { ProfileEditView } from "./ProfileEditView";
import { getResult as getResultIDB } from "../utils/photo-store";

function HistoryResultThumb({ entry, className = "ps-tryon-history-result-img" }: { entry: HistoryEntry; className?: string }) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    let objectUrl: string | null = null;
    setBlobUrl(null);

    if (!entry.hasResult || entry.resultImageUrl) return undefined;

    void getResultIDB(entry.id).then((blob) => {
      if (!alive || !blob) return;
      objectUrl = URL.createObjectURL(blob);
      setBlobUrl(objectUrl);
    });

    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [entry.id, entry.hasResult, entry.resultImageUrl]);

  const src = entry.resultImageUrl || blobUrl;
  if (!src) return null;
  return <img src={src} alt="" className={className} />;
}

export function DrawerPanel({
  drawer, setDrawer, profiles, setProfiles, history, setHistory, setProfileDetail, restoreHistory, t,
}: {
  drawer: DrawerType;
  setDrawer: (d: DrawerType) => void;
  profiles: Profile[];
  setProfiles: React.Dispatch<React.SetStateAction<Profile[]>>;
  history: HistoryEntry[];
  setHistory: React.Dispatch<React.SetStateAction<HistoryEntry[]>>;
  setProfileDetail: (p: Profile | null) => void;
  restoreHistory: (entry: HistoryEntry) => void;
  t: TranslateFn;
}) {
  const [creatingProfile, setCreatingProfile] = useState(false);

  if (!drawer) return null;

  const isMultiSection = (entry: HistoryEntry) =>
    !!(entry.sizeGuide?.sections && Object.keys(entry.sizeGuide.sections).length > 1);

  const handleCreateProfile = (data: Omit<Profile, "id" | "createdAt">) => {
    const newProfile: Profile = {
      ...data,
      id: Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
      createdAt: Date.now(),
    };
    setProfiles(prev => [newProfile, ...prev]);
    setCreatingProfile(false);
  };

  return (
    <div className={`ps-tryon-drawer${drawer ? " ps-tryon-drawer-open" : ""}${drawer === "history" ? " ps-tryon-history-drawer" : ""}`}>
      <div className="ps-tryon-drawer-header">
        <button className="ps-tryon-drawer-back" onClick={() => { setCreatingProfile(false); setDrawer(null); }}><ArrowLeftIcon /></button>
        <span className="ps-tryon-drawer-title">
          {creatingProfile ? t("New Profile") : drawer === "profiles" ? t("My Profiles") : t("Your try-on history")}
        </span>
        {drawer === "profiles" && !creatingProfile && (
          <button className="ps-tryon-drawer-add-btn" onClick={() => setCreatingProfile(true)}>+</button>
        )}
        {drawer === "history" && (
          <button className="ps-tryon-drawer-close" onClick={() => setDrawer(null)} aria-label={t("Close")}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        )}
      </div>
      <div className="ps-tryon-drawer-list">
        {drawer === "profiles" && creatingProfile ? (
          <ProfileEditView onSave={handleCreateProfile} onCancel={() => setCreatingProfile(false)} t={t} />
        ) : drawer === "profiles" ? (
          <>
            <button className="ps-tryon-drawer-create" onClick={() => setCreatingProfile(true)}>
              + {t("Create New Profile")}
            </button>
            {profiles.length === 0 ? (
              <div className="ps-tryon-drawer-empty">{t("No saved profiles yet.")}</div>
            ) : profiles.map(p => (
            <div key={p.id} className="ps-tryon-profile-item" onClick={() => { setProfileDetail(p); setDrawer(null); }}>
              <div className="ps-tryon-profile-avatar"><UserIcon size={20} /></div>
              <div className="ps-tryon-profile-info">
                <div className="ps-tryon-profile-name">{p.name}</div>
                <div className="ps-tryon-profile-detail">
                  {p.gender === "female" ? t("Women's") : t("Men's")}
                  {p.heightCm ? ` \u00b7 ${Math.round(p.heightCm)}${t("cm")}` : ""}
                </div>
              </div>
              <ChevronRightIcon />
            </div>
          ))}
          </>
        ) : (
          <>
          {history.length === 0 ? (
            <div className="ps-tryon-drawer-empty">{t("No history yet.")}</div>
          ) : (<>
            <div className="ps-tryon-history-gallery">
              {history.map((entry, idx) => (
                <div key={entry.id} className="ps-tryon-history-card">
                  <button type="button" className="ps-tryon-history-card-main" onClick={() => restoreHistory(entry)}>
                    <div className="ps-tryon-history-card-media">
                      {(entry.resultImageUrl || entry.hasResult) ? (
                        <HistoryResultThumb entry={entry} className="ps-tryon-history-card-img" />
                      ) : entry.productImage ? (
                        <img src={entry.productImage} alt="" className="ps-tryon-history-card-img" />
                      ) : (
                        <div className="ps-tryon-history-card-placeholder" />
                      )}
                      {entry.productImage && (
                        <img src={entry.productImage} alt="" className="ps-tryon-history-card-product" />
                      )}
                    </div>
                    <span className="ps-tryon-history-card-title">{entry.productTitle || t("Product")}</span>
                    <span className="ps-tryon-history-card-meta">
                      {entry.profileName ? `${entry.profileName} \u00b7 ` : ""}{new Date(entry.date).toLocaleDateString()}
                    </span>
                    {entry.recommendedSize && !isMultiSection(entry) && (
                      <span className="ps-tryon-history-card-size">{entry.recommendedSize}</span>
                    )}
                    {isMultiSection(entry) && entry.sizingResult?.sections && (
                      <span className="ps-tryon-history-card-sections">
                        {Object.entries(entry.sizingResult.sections).slice(0, 3).map(([name, sec]) => `${name}: ${sec.recommendedSize}`).join(" / ")}
                      </span>
                    )}
                  </button>
                  <button className="ps-tryon-history-delete ps-card-delete" onClick={e => { e.stopPropagation(); setHistory(prev => prev.filter((_, i) => i !== idx)); }} aria-label={t("Delete")}>
                    <TrashIcon />
                  </button>
                </div>
              ))}
            </div>
            <button className="ps-tryon-drawer-clear" onClick={() => setHistory([])}>{t("Clear All")}</button>
          </>)}
          </>
        )}
      </div>
    </div>
  );
}
